import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { salesService, sitesService } from '../services';
import { useSettings } from '../contexts/SettingsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import {
  Plus, Search, Loader2, Trash2, Eye, ShoppingCart,
  ChevronLeft, ChevronRight, ArrowLeft
} from 'lucide-react';
import { getApiError } from '@/lib/utils';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'posted', label: 'Posted' },
];

export default function SalesRegisterPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { currencySymbol } = useSettings();
  const [sales, setSales] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [siteFilter, setSiteFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [sites, setSites] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const limit = 25;

  useEffect(() => {
    sitesService.list().then(s => setSites(Array.isArray(s) ? s : (s?.items || []))).catch(() => {});
  }, []);

  const loadSales = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, limit };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (siteFilter !== 'all') params.site_id = siteFilter;
      if (search) params.search = search;
      const res = await salesService.list(params);
      setSales(res.items || []);
      setTotal(res.total || 0);
    } catch {
      toast({ title: 'Error', description: 'Failed to load sales', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, siteFilter, search, toast]);

  useEffect(() => { loadSales(); }, [loadSales]);

  const handleDelete = async (e, saleId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this sale record?')) return;
    setDeleting(saleId);
    try {
      await salesService.delete(saleId);
      toast({ title: 'Deleted', description: 'Sale deleted' });
      loadSales();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to delete'), variant: 'destructive' });
    } finally {
      setDeleting(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-6" data-testid="sales-register-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/stocktaking/dashboard')} data-testid="back-to-dashboard">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ShoppingCart className="h-6 w-6 text-slate-600" />
              Sales Register
            </h1>
            <p className="text-sm text-muted-foreground">{total} record{total !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <Button onClick={() => navigate('/stocktaking/sales/new')} data-testid="new-sale-btn">
          <Plus className="h-4 w-4 mr-2" /> New Sale
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-3 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search reference..." className="pl-9 h-9"
              value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
              data-testid="search-input" />
          </div>
          <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[140px] h-9" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={siteFilter} onValueChange={v => { setSiteFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[180px] h-9" data-testid="site-filter">
              <SelectValue placeholder="All Sites" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sites</SelectItem>
              {sites.map(s => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : sales.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">No sales found</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Reference</TableHead>
                  <TableHead>Site</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sales.map(s => (
                  <TableRow key={s.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/stocktaking/sales/${s.id}`)}
                    data-testid={`sale-row-${s.id}`}>
                    <TableCell className="font-medium">{s.reference_number || '-'}</TableCell>
                    <TableCell>{s.site_name || '-'}</TableCell>
                    <TableCell>{s.sales_date}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {s.period_from && s.period_to ? `${s.period_from} – ${s.period_to}` : '-'}
                    </TableCell>
                    <TableCell className="text-right font-medium">{currencySymbol}{(s.lines_total || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant={s.status === 'posted' ? 'default' : 'secondary'}
                        className={s.status === 'posted' ? 'bg-emerald-100 text-emerald-800' : ''}>
                        {s.status === 'posted' ? 'Posted' : 'Draft'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" onClick={() => navigate(`/stocktaking/sales/${s.id}`)}
                          data-testid={`view-sale-${s.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        {s.status !== 'posted' && (
                          <Button variant="ghost" size="icon" onClick={e => handleDelete(e, s.id)}
                            className="text-red-500 hover:text-red-700" disabled={deleting === s.id}
                            data-testid={`delete-sale-${s.id}`}>
                            {deleting === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-1">
          <p className="text-sm text-muted-foreground">Page {page + 1} of {totalPages}</p>
          <div className="flex gap-1">
            <Button variant="outline" size="icon" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
